import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSession } from '@/contexts/SessionContext';
import { ApiError, fetchInitData, type InitData } from '@/services/api';

/**
 * Данные из GET /init: личность вошедшего и первые страницы разделов.
 * Загружается один раз после успешного входа и сбрасывается при выходе.
 */

export type InitDataStatus = 'idle' | 'loading' | 'ready' | 'error';

interface InitDataContextValue {
  status: InitDataStatus;
  data: InitData | null;
  errorCode: string | null;
  reload: () => void;
}

const InitDataContext = createContext<InitDataContextValue | null>(null);

interface InitDataProviderProps {
  children: ReactNode;
}

export function InitDataProvider({ children }: InitDataProviderProps) {
  const { isAuthenticated } = useSession();
  const [status, setStatus] = useState<InitDataStatus>('idle');
  const [data, setData] = useState<InitData | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      setStatus('idle');
      setData(null);
      setErrorCode(null);
      return;
    }

    const controller = new AbortController();
    setStatus('loading');
    setErrorCode(null);

    void fetchInitData(controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) return;
        setData(loaded);
        setStatus('ready');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('[Unit1 Admin] Не удалось загрузить /init.', error);
        setErrorCode(error instanceof ApiError ? error.code : 'INTERNAL');
        setStatus('error');
      });

    return () => controller.abort();
  }, [isAuthenticated, reloadNonce]);

  const reload = useCallback(() => setReloadNonce((nonce) => nonce + 1), []);

  const value = useMemo<InitDataContextValue>(
    () => ({ status, data, errorCode, reload }),
    [status, data, errorCode, reload],
  );

  return <InitDataContext.Provider value={value}>{children}</InitDataContext.Provider>;
}

export function useInitData(): InitDataContextValue {
  const context = useContext(InitDataContext);
  if (!context) {
    throw new Error('useInitData must be used within InitDataProvider');
  }
  return context;
}
